import { Link, NavLink, Outlet } from 'react-router-dom';
import {
  Compass,
  GraduationCap,
  House,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  Target,
} from 'lucide-react';
import { cn } from '@mlm/ui/lib/cn';
import { useAuthStore } from '../lib/auth-store';
import { tierLabel } from '../lib/pro';
import { useEntitlement, useTierLabels, useToday } from '../lib/queries';
import { useReminderSync } from '../lib/use-reminder-sync';
import { BrandMark, StarGlyph } from './visual';

const HUBS = [
  { to: '/home', label: 'Home', icon: House },
  { to: '/coach', label: 'Coach', icon: MessageCircle },
  { to: '/manifest', label: 'Manifest', icon: Sparkles },
  { to: '/align', label: 'Align', icon: Compass },
  { to: '/achieve', label: 'Achieve', icon: Target },
] as const;

// The two portal sides, kept apart on purpose: Shop is the MLM business
// (catalogue, orders, wallet, network) and Learn is the academy. They share no
// screens and a member is in one mindset or the other, so folding them into
// one entry would bury whichever they did not come for.
const STORE = [
  { to: '/shop', label: 'Shop', icon: ShoppingBag },
  { to: '/learn', label: 'Learn', icon: GraduationCap },
] as const;

// The redesign's phone bar defaults to the five hubs, with Shop and Learn
// reached from Home's rails and the profile; its seven-tab variant keeps them
// in the bar. Seven it is until the client asks otherwise: the store side is
// where members' orders and wallets live.
const PHONE_TABS = [...HUBS, ...STORE] as const;

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde';

function SideLink({ tab }: { tab: (typeof PHONE_TABS)[number] }) {
  return (
    <NavLink
      to={tab.to}
      className={({ isActive }) =>
        cn(
          'flex min-h-11 items-center gap-3 rounded-full px-3.5 font-mono text-[0.68rem] font-medium uppercase tracking-[0.14em] transition-colors',
          focusRing,
          isActive
            ? 'bg-wash text-ink [&_svg]:text-verde'
            : 'text-sage hover:bg-surface hover:text-ink',
        )
      }
    >
      <tab.icon className="h-[18px] w-[18px]" strokeWidth={1.6} aria-hidden />
      {tab.label}
    </NavLink>
  );
}

/**
 * The hubs (blueprint "Global Design & Interaction Rules", restyled in the
 * 2026-10 redesign). Phone: a brand header and a bottom tab bar on blurred
 * paper. Desktop (`lg`): a fixed 248px sidebar with pill navigation, the store
 * side as its own group, a way into a session and the profile at its foot;
 * the header and tab bar are hidden.
 */
export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const today = useToday();
  const entitlement = useEntitlement();
  const labels = useTierLabels();
  useReminderSync();
  const name = today.data?.profile.firstName ?? user?.fullName ?? user?.email ?? '';
  const initial = name.slice(0, 1) || '·';
  const plan = entitlement.data ? tierLabel(entitlement.data.tier, labels) : null;
  const avatar =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-verde/25 bg-wash font-mono text-xs uppercase text-verde';

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] flex-col justify-between border-r border-border bg-background px-3.5 pb-6 pt-7 lg:flex">
        <div className="min-h-0 overflow-y-auto">
          <Link
            to="/home"
            className={cn('flex items-center gap-2.5 rounded-full px-2.5', focusRing)}
          >
            <BrandMark size={30} />
            <span className="font-display text-[19px] font-semibold tracking-[-0.01em] text-ink">
              Miracles Protocol
            </span>
          </Link>
          <nav aria-label="Primary" className="mt-[34px] flex flex-col gap-1">
            {HUBS.map((tab) => (
              <SideLink key={tab.to} tab={tab} />
            ))}
            <p className="mx-3.5 mb-1.5 mt-[18px] font-mono text-[0.56rem] uppercase tracking-[0.16em] text-sage-deep">
              Store &amp; academy
            </p>
            {STORE.map((tab) => (
              <SideLink key={tab.to} tab={tab} />
            ))}
          </nav>
        </div>
        <div>
          <Link
            to="/align/breathwork"
            className={cn(
              'flex min-h-[46px] items-center justify-center gap-2 rounded-full bg-gold font-mono text-[0.64rem] uppercase tracking-[0.14em] text-ink transition-colors hover:bg-[#c29d4c]',
              focusRing,
            )}
          >
            <StarGlyph color="#10201A" />
            Start a session
          </Link>
          <Link
            to="/profile"
            className={cn(
              'group mt-[18px] flex items-center gap-3 border-t border-border px-2.5 pt-4',
              focusRing,
            )}
          >
            <span className={avatar}>{initial}</span>
            <span className="min-w-0">
              <span className="block truncate text-sm text-ink transition-colors group-hover:text-verde">
                {name || 'Profile'}
              </span>
              <span className="mt-0.5 block truncate font-mono text-[0.58rem] uppercase tracking-[0.14em] text-sage-deep">
                {plan ? `${plan} · Settings` : 'Profile and settings'}
              </span>
            </span>
          </Link>
        </div>
      </aside>

      <header className="sticky top-0 z-30 bg-canvas/[0.92] backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-xl items-center justify-between px-5 pb-2 pt-3">
          <Link to="/home" className={cn('flex items-center gap-2.5 rounded-full', focusRing)}>
            <BrandMark size={28} />
            <span className="font-display text-[17px] font-semibold tracking-[-0.01em] text-ink">
              Miracles Protocol
            </span>
          </Link>
          <Link
            to="/profile"
            aria-label="Profile and settings"
            className={cn(avatar, 'transition-colors hover:border-verde', focusRing)}
          >
            {initial}
          </Link>
        </div>
      </header>
      <main className="flex-1 pb-24 lg:pb-0 lg:pl-[248px]">
        <div className="lg:px-9 lg:pb-16 lg:pt-11">
          <Outlet />
        </div>
      </main>
      <nav
        aria-label="Primary"
        data-bottom-bar
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-canvas/[0.92] px-1.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1.5 backdrop-blur-md lg:hidden"
      >
        <ul className="mx-auto flex max-w-xl">
          {PHONE_TABS.map((tab) => (
            <li key={tab.to} className="min-w-0 flex-1">
              <NavLink
                to={tab.to}
                className={({ isActive }) =>
                  cn(
                    'group flex flex-col items-center gap-1 pb-0.5 pt-2 font-mono text-[0.5rem] uppercase tracking-[0.04em] transition-colors min-[380px]:text-[0.54rem] min-[380px]:tracking-[0.08em]',
                    focusRing,
                    isActive ? 'text-ink' : 'text-sage hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-7 w-11 items-center justify-center rounded-full transition-colors',
                        isActive && 'bg-wash text-verde',
                      )}
                    >
                      <tab.icon className="h-5 w-5" strokeWidth={1.8} aria-hidden />
                    </span>
                    {tab.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
